import axios from 'axios';
import axiosClient from './axiosClient';

// Plain axios instance (no auth header) for the public registration endpoint
const publicApi = axios.create({
  baseURL: '/api/v1',
  headers: { 'Content-Type': 'application/json' },
});

export type RegistrationStatus = 'PENDING' | 'APPROVED' | 'REJECTED';

export interface RegistrationRequestDto {
  // Inbound (submit)
  fullName?: string;
  username?: string;
  email?: string;
  requestedRole?: string;
  message?: string;
  // Admin actions
  assignedRole?: string;
  rejectionReason?: string;
  // Response
  id?: string;
  status?: RegistrationStatus;
  reviewedByName?: string;
  reviewedAt?: string;
  createdAt?: string;
  tempPassword?: string;
  emailSent?: boolean;
  keycloakRoleAssigned?: boolean;
}

export const registrationApi = {
  /** Public — check if a username is already taken */
  checkUsername: (username: string) =>
    publicApi.get<{ success: boolean; data: { exists: boolean } }>(
      '/auth/check-username',
      { params: { username } }
    ),

  /** Public — no auth required */
  submit: (data: RegistrationRequestDto) =>
    publicApi.post<{ success: boolean; message: string; data: RegistrationRequestDto }>(
      '/auth/register',
      data
    ),

  /** Admin — list all requests (optionally filter by status) */
  list: (params?: { status?: RegistrationStatus; page?: number; size?: number }) =>
    axiosClient.get<{ success: boolean; data: { content: RegistrationRequestDto[]; totalElements: number } }>(
      '/admin/registrations',
      { params }
    ),

  /** Admin — pending count for badge */
  getPendingCount: () =>
    axiosClient.get<{ success: boolean; data: { count: number } }>(
      '/admin/registrations/pending-count'
    ),

  /** Admin — approve and assign role */
  approve: (id: string, assignedRole: string) =>
    axiosClient.put<{ success: boolean; message: string; data: RegistrationRequestDto }>(
      `/admin/registrations/${id}/approve`,
      { assignedRole }
    ),

  /** Admin — reject with reason */
  reject: (id: string, rejectionReason: string) =>
    axiosClient.put<{ success: boolean; message: string; data: RegistrationRequestDto }>(
      `/admin/registrations/${id}/reject`,
      { rejectionReason }
    ),

  /** Admin — delete a registration request */
  delete: (id: string) =>
    axiosClient.delete(`/admin/registrations/${id}`),
};
